import { Component, inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core'
import { TableData } from '../../../../models/table-data'
import { InlineEditCellDirective } from '../inline-edit-cell'
import EntityUrlsUtils from '../../../../../../utils/entity-urls.util'
import { EMPTY, Observable, of, Subscription } from 'rxjs'
import { take, switchMap } from 'rxjs/operators'
import { Field } from '../../../../models/field'
import { AdditionalQualifier } from '../../../../models/table-state.model'
import { EntityReference } from '../../../../models/entity-reference'
import { ConfirmationService } from 'primeng/api'
import { map } from 'rxjs/operators'
import { EntityLinkService } from '../../../../../app-menu/services/entity-link.service'
import { StoredEntityService } from '../../../../../entity-store-access/services/stored-entity.service'
import { SoftFilter } from '../../../../models/softfilter'
import { EntityService } from '../../../../services/entity.service'

@Component({
  selector: 'jml-lib-entity-reference-cell',
  templateUrl: './entity-reference-cell.component.html',
  styleUrls: ['../inline-edit-cell.scss']
})
export class EntityReferenceCellComponent extends InlineEditCellDirective implements OnDestroy, OnChanges {
  @Input() field: Field
  @Input() dtoType: string
  @Input() dialogHeader: string

  displayEntitySelectionDialog = false
  entitySelectionTableData: TableData
  entityDisplayName: string
  subscriptions: Subscription[] = []
  entitySub: Subscription

  targetUrl$: Observable<string> = EMPTY
  targetParams$: Observable<any> = EMPTY
  isEntityDeleted = false

  private entityLinkService = inject(EntityLinkService)
  private storedEntityService = inject(StoredEntityService)
  private entityService = inject(EntityService)

  ngOnChanges(changes: SimpleChanges) {
    super.ngOnChanges(changes)

    const entityType = !!changes.fields ? changes.field.currentValue.referenceType : this.field.referenceType
    const id = !!changes.rowData ? changes.rowData.currentValue[this.fieldName]?.id : this.rowData[this.fieldName]?.id

    if (!!entityType && !!id) {
      const linkData$ = this.entityLinkService.getEntityLinkData$({ type: entityType, id })
      this.targetUrl$ = linkData$.pipe(map((linkData) => linkData.url))
      this.targetParams$ = linkData$.pipe(map((linkData) => linkData.queryParams))
    }

    const rd = !!changes.rowData ? changes.rowData.currentValue : this.rowData
    const fn = !!changes.fieldName ? changes.fieldName.currentValue : this.fieldName

    this.entityDisplayName = rd[fn] && typeof rd[fn]['_repr_'] === 'string' ? rd[fn]['_repr_'] : ''

    if (!!entityType && !!id) {
      if (!!this.entitySub) {
        this.entitySub.unsubscribe()
      }
      this.entitySub = this.storedEntityService
        .getEntity$(entityType, +id, { partialEntityIsSufficient: true })
        .subscribe((entity) => {
          this.entityDisplayName = entity?.fields['_repr_']
          this.isEntityDeleted = !!entity?.fields['deleted']
        })
    }

    // Changes in entity data may cause (soft-)filters to change for this reference.
    // We need to rebuild the table data to make sure these changes are applied to the filters.
    if (!!changes.rowData) {
      this.entitySelectionTableData = undefined
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe())
  }

  processData() {
    // The dynamic table API delivers references as a tuple containing an id and a representation. Attributes only deliver a string, we need to check how the value is delivered and display accordingly
    if (this.rowData[this.fieldName] && typeof this.rowData[this.fieldName]['_repr_'] === 'string') {
      return this.rowData[this.fieldName]['_repr_']
    } else {
      return this.rowData[this.fieldName]
    }
  }

  entitySelected(entity: any) {
    this.rowData[this.fieldName] = { id: entity['id'], _repr_: entity['_repr_'] }
    this.entityDisplayName = entity['_repr_']
    this.displayEntitySelectionDialog = false
    this.completeCellEdit.emit({ data: this.rowData, field: this.fieldName })
  }

  openEntitySelectionDialog() {
    if (!this.entitySelectionTableData) {
      let hardFilter: AdditionalQualifier
      const entityRef = new EntityReference(this.dtoType, this.rowData['id'])
      if (this.field.referenceFilter) {
        hardFilter = new AdditionalQualifier(this.field.referenceFilter).setEntityRef(entityRef)
      }
      of(this.field.referenceSoftFilter)
        .pipe(
          take(1),
          switchMap((filter) => {
            if (filter?.includes('${')) {
              return this.entityService.eval(filter, undefined, entityRef).pipe(map((x) => x.result))
            } else {
              return of(filter)
            }
          })
        )
        .subscribe((referenceSoftFilter) => {
          this.entitySelectionTableData = new TableData(this.field.referenceType)
            .isEntitySelectionDialog()
            .hideHeader()
            .hideAttachments()
            .setScrollable()
            .disableInlineEdit()
            .setScrollHeight(window.innerHeight - 350 + 'px')
            .setFilters(hardFilter, !!referenceSoftFilter ? new SoftFilter(entityRef, referenceSoftFilter) : null)
          this.displayEntitySelectionDialog = true
        })
    } else {
      this.displayEntitySelectionDialog = true
    }
  }

  triggerEdit() {
    this.openEntitySelectionDialog()
  }

  generateRouteUrl = (entityType: string): string[] => EntityUrlsUtils.generateUrlForEntityType(entityType)
  generateRouteQueryParams = (entityType: string, id: number): Object =>
    EntityUrlsUtils.generateQueryParamsForEntityType({ entityType, id })
}
